// SPDX-License-Identifier: AGPL-3.0-or-later

import {TextualAttachmentPreview} from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreview';
import {splitFilename} from '@app/features/channel/components/embeds/EmbedUtils';
import {canDeleteAttachmentUtil} from '@app/features/channel/components/MessageActionUtils';
import {useMaybeMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import {useDeleteAttachment} from '@app/features/messaging/hooks/useDeleteAttachment';
import type {Message} from '@app/features/messaging/models/Message';
import {shouldPreviewAttachment} from '@app/features/messaging/utils/AttachmentPreviewUtils';
import {downloadFile} from '@app/features/messaging/utils/FileDownloadUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import attachmentFileStyles from '@app/features/theme/styles/AttachmentFile.module.css';
import messageStyles from '@app/features/theme/styles/Message.module.css';
import {MediaContextMenu} from '@app/features/ui/action_menu/MediaContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {MessageAttachment} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {useLingui} from '@lingui/react/macro';
import {
	DownloadSimpleIcon,
	FileArchiveIcon,
	FileAudioIcon,
	FileCodeIcon,
	FileIcon,
	FileImageIcon,
	FilePdfIcon,
	FilePptIcon,
	FileTextIcon,
	FileVideoIcon,
	FileXlsIcon,
	TrashIcon,
	WarningCircleIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

interface AttachmentFileProps {
	attachment: MessageAttachment;
	isPreview?: boolean;
	message?: Message;
}
export const AttachmentFile = observer(({attachment, message, isPreview}: AttachmentFileProps) => {
	const {t} = useLingui();
	const {enabled: isMobile} = MobileLayout;
	const isExpired = Boolean(attachment.expired);
	const fileName = attachment.title || attachment.filename;
	const fileSize = formatFileSize(attachment.size);
	const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
	const {name: fileNameWithoutExt, extension: fileExt} = splitFilename(fileName);
	const showTextPreview = !isPreview && shouldPreviewAttachment(attachment);
	const getFileTypeIcon = () => {
		const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
		const textTypes = ['txt', 'rtf', 'md', 'log'];
		const archiveTypes = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'];
		const audioTypes = ['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'wma'];
		const videoTypes = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv'];
		const codeTypes = [
			'js',
			'jsx',
			'ts',
			'tsx',
			'py',
			'java',
			'c',
			'cpp',
			'h',
			'css',
			'html',
			'json',
			'xml',
			'yml',
			'yaml',
			'sh',
			'go',
			'rs',
			'rb',
			'php',
		];
		const excelTypes = ['xls', 'xlsx', 'csv'];
		const presentationTypes = ['ppt', 'pptx'];
		const documentTypes = ['doc', 'docx'];
		if (imageTypes.includes(fileExtension)) return <FileImageIcon size={32} />;
		if (fileExtension === 'pdf') return <FilePdfIcon size={32} />;
		if (textTypes.includes(fileExtension)) return <FileTextIcon size={32} />;
		if (documentTypes.includes(fileExtension)) return <FileTextIcon size={32} />;
		if (archiveTypes.includes(fileExtension)) return <FileArchiveIcon size={32} />;
		if (audioTypes.includes(fileExtension)) return <FileAudioIcon size={32} />;
		if (videoTypes.includes(fileExtension)) return <FileVideoIcon size={32} />;
		if (codeTypes.includes(fileExtension)) return <FileCodeIcon size={32} />;
		if (excelTypes.includes(fileExtension)) return <FileXlsIcon size={32} />;
		if (presentationTypes.includes(fileExtension)) return <FilePptIcon size={32} />;
		return <FileIcon size={32} />;
	};
	let containerStyles: React.CSSProperties;
	if (isMobile) {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: '100%',
			minWidth: 0,
		};
	} else if (showTextPreview) {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: '50vw',
			minWidth: 0,
		};
	} else {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: '400px',
			minWidth: 'min(400px, 100%)',
		};
	}
	const handleDownload = async (e: React.MouseEvent) => {
		e.preventDefault();
		const downloadUrl = attachment.proxy_url ?? attachment.url;
		if (!downloadUrl || isExpired) return;
		await downloadFile(downloadUrl, 'file', fileName);
	};
	const handleDelete = useDeleteAttachment(message, attachment.id);
	const canDelete = canDeleteAttachmentUtil(message) && !isMobile;
	const showDeleteButton = canDelete && !isPreview;
	const messageViewContext = useMaybeMessageViewContext();
	const handleMouseDown = () => {
		window.getSelection()?.removeAllRanges();
	};
	const handleContextMenu = (e: React.MouseEvent) => {
		if (!message || isPreview) return;
		e.preventDefault();
		e.stopPropagation();
		ContextMenuCommands.openFromEvent(e, ({onClose}) => (
			<MediaContextMenu
				message={message}
				originalSrc={attachment.url ?? ''}
				proxyURL={attachment.proxy_url ?? undefined}
				type="file"
				contentHash={attachment.content_hash}
				attachmentId={attachment.id}
				defaultName={attachment.filename}
				defaultAltText={attachment.filename}
				onClose={onClose}
				onDelete={isPreview ? () => {} : (messageViewContext?.handleDelete ?? (() => {}))}
			/>
		));
	};
	return (
		<div
			role="group"
			style={containerStyles}
			className={attachmentFileStyles.container}
			onContextMenu={handleContextMenu}
			onMouseDown={handleMouseDown}
		>
			{showDeleteButton && (
				<button
					type="button"
					onClick={handleDelete}
					className={clsx(messageStyles.hoverAction, attachmentFileStyles.deleteButton)}
					aria-label={t`Delete Attachment`}
				>
					<TrashIcon size={16} weight="bold" />
				</button>
			)}
			{showTextPreview ? (
				<TextualAttachmentPreview attachment={attachment} />
			) : (
				<div className={attachmentFileStyles.attachmentContainer}>
					<div className={attachmentFileStyles.iconContainer}>{getFileTypeIcon()}</div>
					<div className={attachmentFileStyles.fileInfoContainer}>
						<p className={attachmentFileStyles.fileName}>
							<span className={attachmentFileStyles.fileNameTruncate}>{fileNameWithoutExt}</span>
							<span className={attachmentFileStyles.fileExtension}>{fileExt}</span>
						</p>
						<p className={attachmentFileStyles.fileSize}>{fileSize}</p>
					</div>
					{isExpired ? (
						<Tooltip text={t`Attachment expired`}>
							<div className={clsx(attachmentFileStyles.downloadButton, attachmentFileStyles.downloadButtonDisabled)}>
								<WarningCircleIcon size={20} weight="bold" />
							</div>
						</Tooltip>
					) : (
						<button
							type="button"
							onClick={handleDownload}
							className={attachmentFileStyles.downloadButton}
							aria-label={t`Download`}
							disabled={!attachment.url}
						>
							<DownloadSimpleIcon size={20} weight="bold" />
						</button>
					)}
				</div>
			)}
		</div>
	);
});
